<template>
  <div v-if="mask && prefix">
    <ValidationProvider
      mode="lazy"
      :rules="{
        regex: convertToRegex(mask),
        required: true,
      }"
      v-slot="{ errors, validated, valid }"
    >
      <b-field
        v-bind="$attrs"
        :type="{
          'is-danger': errors[0],
          'is-success': validated && valid,
        }"
        label-position="on-border"
        :message="errors"
        class="validator-field"
        :class="fieldClass"
        :label="label"
        expanded
      >
        <div class="control phone-prefix">
          <span
            class="button is-static"
            :class="{
              'is-danger': errors[0],
              'is-success': validated && valid,
            }"
          >
            +{{ prefix }}
          </span>
        </div>
        <b-input
          v-model="formattedValue"
          type="text"
          v-bind="$attrs"
          v-mask="mask"
          class="phone-input"
          expanded
        />
      </b-field>
    </ValidationProvider>
  </div>
  <div v-else>
    <ValidationProvider
      mode="lazy"
      :name="validationProviderName"
      :rules="{
        regex: convertToRegex(convertMask(selectedCountry.phoneFormat)),
        required: true,
      }"
      v-slot="{ errors, validated, valid }"
    >
      <b-field
        v-bind="$attrs"
        :type="{
          'is-danger': errors[0],
          'is-success': validated && valid,
        }"
        label-position="on-border"
        :message="errors"
        class="validator-field"
        :class="fieldClass"
        :label="label"
        expanded
      >
        <div class="control phone-prefix">
          <span
            class="button is-static"
            :class="{
              'is-danger': errors[0],
              'is-success': validated && valid,
            }"
          >
            +{{ selectedCountry.phoneCountryCode }}
          </span>
        </div>
        <b-input
          v-model="formattedValue"
          type="text"
          v-bind="$attrs"
          v-mask="convertMask(selectedCountry.phoneFormat)"
          class="phone-input"
          expanded
        />
        <b-dropdown
          v-if="countries"
          class="country-selector control"
          position="is-bottom-left"
          :mobile-modal="false"
          v-model="selectedCountryId"
        >
          <template
            v-if="selectedCountry.phoneCountryCode"
            #trigger="{ active }"
          >
            <button
              class="button input"
              :class="{
                'is-danger': errors[0],
                'is-success': validated && valid,
              }"
              @click.prevent
            >
              <div class="button-content">
                <span
                  v-html="getFlag(selectedCountry.flag)"
                  class="flag"
                ></span>
                <!-- <span>+{{ selectedCountry.phoneCountryCode }}</span> -->
              </div>
              <b-icon
                icon-pack="fa"
                :icon="active ? 'chevron-up' : 'chevron-down'"
                size="is-small"
              ></b-icon>
            </button>
          </template>

          <template v-else #trigger="{ active }">
            <button
              class="button input"
              :class="{
                'is-danger': errors[0],
                'is-success': validated && valid,
              }"
              @click.prevent
            >
              <div class="button-content">
                <span>{{
                  $t("mp.phone_input.country_select.placeholder")
                }}</span>
              </div>
              <b-icon
                icon-pack="fa"
                :icon="active ? 'chevron-up' : 'chevron-down'"
                size="is-small"
              ></b-icon>
            </button>
          </template>

          <b-dropdown-item
            v-for="country in countries"
            :key="country.id"
            :value="country.id"
          >
            <div class="country-list-item">
              <div v-html="getFlag(country.flag)" class="flag"></div>
              <div>{{ country.name }} (+{{ country.phoneCountryCode }})</div>
            </div>
          </b-dropdown-item>
        </b-dropdown>
      </b-field>
    </ValidationProvider>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import twemoji from "@twemoji/api";

export default {
  props: {
    label: {
      type: String,
    },
    value: {
      type: [String, Number],
      default: "",
    },
    fieldClass: {
      type: String,
      default: "",
    },
    prefix: {
      type: String,
    },
    mask: {
      type: String,
    },
    validationProviderName: {
      type: String,
    },
  },
  data() {
    return {
      formattedValue: null,
      selectedCountryId: null,
    };
  },
  watch: {
    formattedValue(value) {
      let newValue = null;
      if (this.prefix && this.mask) {
        newValue = this.prefix + value.replaceAll(/\D/g, "");
      } else {
        newValue =
          this.selectedCountry.phoneCountryCode + value.replaceAll(/\D/g, "");
      }
      this.$emit("input", newValue);
    },
    value: {
      immediate: true,
      handler: function (newVal) {
        if (newVal) {
          if (this.prefix && this.mask) {
            if (newVal.startsWith(this.prefix)) {
              newVal = newVal.substring(this.prefix.length, newVal.length);
            }
            this.formattedValue = this.fillMask(this.mask, newVal);
          } else {
            if (newVal.startsWith(this.selectedCountry.phoneCountryCode)) {
              newVal = newVal.substring(
                this.selectedCountry.phoneCountryCode.length,
                newVal.length
              );
            }
            this.formattedValue = this.fillMask(
              this.convertMask(this.selectedCountry.phoneFormat),
              newVal
            );
          }
        }
      },
    },
  },
  computed: {
    ...mapGetters({
      countries: "i18n/countries",
      defaultCountry: "i18n/country",
    }),
    selectedCountry() {
      return (
        this.countries.find(
          (country) => country.id === this.selectedCountryId
        ) ?? { phoneFormat: "" }
      );
    },
  },
  methods: {
    convertToRegex(mask) {
      let regex = "";
      let counter = 0;
      for (const char of mask) {
        if (char !== "#") {
          if (counter > 0) {
            regex += `\\d{${counter}}`;
            counter = 0;
          }
          if (char === " ") {
            regex += "\\s";
          } else {
            regex += `\\${char}`;
          }
        } else {
          ++counter;
        }
      }
      if (counter > 0) {
        regex += `\\d{${counter}}`;
      }
      return new RegExp(regex);
    },
    fillMask(mask, value) {
      if (typeof value === "string") {
        value = value.split("");
        value.reverse();
      }
      if (value.length > 0) {
        mask = this.fillMask(mask.replace("#", value.pop()), value);
      }
      return mask;
    },
    getFlag(flag) {
      if (flag) {
        return twemoji.parse(flag, {
          folder: "svg",
          ext: ".svg",
        });
      }
    },
    convertMask(mask) {
      return mask.replaceAll(/\d/g, "#");
    },
  },
  created() {
    this.selectedCountryId = this.defaultCountry.id;
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/colors.scss";

.phone-input-group {
  display: flex;

  .phone-input {
    order: 2;
    width: 100%;

    input {
      margin-left: 0;
      border-top-right-radius: 8px !important;
      border-bottom-right-radius: 8px !important;
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
  }
}
.phone-prefix.control {
  .button {
    &.is-danger {
      border-color: #f14668;
    }

    &.is-success {
      border-color: #48c78e;
    }
  }
}

.country-selector {
  button.button.input {
    display: flex;
    align-items: center;
    justify-content: space-between;

    &.is-danger {
      background: inherit;
      color: inherit;
      border-color: #f14668;
    }

    &.is-success {
      background: inherit;
      color: inherit;
      border-color: #48c78e;
    }

    div.button-content {
      display: flex;
      align-items: center;
      gap: 8px;

      .flag {
        width: 24px;
        height: 24px;
      }
    }
  }
}

.country-list-item {
  display: flex;
  align-items: center;
  gap: 8px;

  .flag {
    width: 24px;
    height: 24px;
  }
}
</style>
